import React from "react";
import {
  CartesianGrid,
  LineChart as LC,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Props as XAxisProps } from "recharts/types/cartesian/XAxis";
import { Props as YAxisProps } from "recharts/types/cartesian/YAxis";

import {
  CustomTooltip,
  CustomXAxisTick,
  CustomYAxisTick,
} from "@/utils/recharts-widgets";

import { LineChartProps } from "./LineChartProps";
import SignalDot from "./SignalDot";

function LineChart({
  id,
  syncId = 0,
  data,
  linesInfo,
  xTicksFormatter,
  yTicksFormatter,
  showGridVertical = false,
  showGridHorizontal = false,
  gridStroke = "#aaa",
  gridStrokeDasharray = "1 0",
  gridStrokeWidth = 2,
  xTicksPaddingTop = 0.5,
  yTicksPaddingRight = 14,
  axisTickColor = "#AEAEAE",
  yAxisWidth = 40,
  xPaddingLeft = 12,
  xPaddingRight = 12,
  yPaddingTop = 0,
  showSignals = false,
  showDots = false,
  showLegend = false,
  showXTicks = false,
  showYTicks = false,
  yAxisInterval = "preserveEnd",
  ticks,
  xAxisHeight = 30,
  tooltipIconType,
  tooltipReverseOrder,
}: LineChartProps) {
  if (!data.length) return <></>;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LC data={data} syncId={syncId} syncMethod="value" key={id} id={id}>
        <CartesianGrid
          vertical={showGridVertical}
          horizontal={showGridHorizontal}
          stroke={gridStroke}
          strokeDasharray={gridStrokeDasharray}
          strokeWidth={gridStrokeWidth}
        />
        <XAxis
          dataKey="name"
          padding={{ left: xPaddingLeft, right: xPaddingRight }}
          tick={(props: XAxisProps) => (
            <CustomXAxisTick
              {...{
                props,
                xTicksPaddingTop,
                xTicksFormatter,
                axisTickColor,
              }}
            />
          )}
          height={xAxisHeight}
          tickLine={showXTicks}
        />
        <YAxis
          width={yAxisWidth}
          padding={{ top: yPaddingTop }}
          tick={(props: YAxisProps) => (
            <CustomYAxisTick
              {...{
                props,
                yTicksPaddingRight,
                yTicksFormatter,
                axisTickColor,
              }}
            />
          )}
          tickLine={showYTicks}
          interval={yAxisInterval}
          ticks={ticks}
        />
        <Tooltip
          wrapperStyle={{ outline: "none" }}
          content={
            <CustomTooltip
              valueFormatter={yTicksFormatter}
              reverseOrder={tooltipReverseOrder}
              iconType={tooltipIconType}
            />
          }
        />
        {Object.keys(linesInfo).map((key) => {
          return (
            <Line
              key={key}
              type={linesInfo[key]?.lineType}
              dataKey={key}
              stroke={linesInfo[key]?.lineStroke}
              strokeWidth={linesInfo[key]?.lineStrokeWidth}
              strokeDasharray={linesInfo[key]?.lineStrokeDasharray}
              activeDot={true}
              dot={
                showSignals ? (
                  <SignalDot />
                ) : showDots ? (
                  { r: 2, fill: linesInfo[key]?.lineStroke }
                ) : (
                  false
                )
              }
              isAnimationActive={false}
            />
          );
        })}
        {showLegend && (
          <Legend
            iconType="plainline"
            verticalAlign="bottom"
            wrapperStyle={{ paddingTop: 12 }}
          />
        )}
      </LC>
    </ResponsiveContainer>
  );
}

export default React.memo(LineChart);
